import type { ImportFile } from '@/app/ai/hooks/useImportFilesToGrid';
import type { ContextMenuState } from '@/app/atoms/contextMenuAtom';
import type { ErrorValidation } from '@/app/gridGL/cells/CellsSheet';
import type { TimerNames } from '@/app/gridGL/helpers/startupTimer';
import type { EditingCell } from '@/app/gridGL/HTMLGrid/hoverCell/HoverCell';
import type { ChangeInputOptions } from '@/app/gridGL/HTMLGrid/inlineEditor/changeInputOptions';
import type { CursorMode } from '@/app/gridGL/HTMLGrid/inlineEditor/inlineEditorKeyboard';
import type {
  CommentThread,
  ConditionalFormatClient,
  JsBordersSheet,
  JsChart,
  JsCodeSummaryRequest,
  JsHashValidationWarnings,
  JsHtmlOutput,
  JsOffset,
  JsRenderCell,
  JsRenderCodeCell,
  JsSheetFill,
  JsUpdateCodeCell,
  SheetBounds,
  SheetInfo,
  Validation,
} from '@/app/quadratic-core-types';
import type { JsMergeCells, SheetContentCache, SheetDataTablesCache } from '@/app/quadratic-core/quadratic_core';
import type { CodeCell } from '@/app/shared/types/codeCell';
import type { RefreshType } from '@/app/shared/types/RefreshType';
import type { SheetPosTS } from '@/app/shared/types/size';
import type { PromptEditorMention } from '@/app/ui/components/promptEditor/PromptEditor';
import type { CodeRun } from '@/app/web-workers/CodeRun';
import type { MultiplayerState } from '@/app/web-workers/multiplayerWebWorker/multiplayerClientMessages';
import type { CellEdit, MultiplayerUser } from '@/app/web-workers/multiplayerWebWorker/multiplayerTypes';
import type {
  CoreClientCellEditHistory,
  CoreClientImage,
  CoreClientImportProgress,
  CoreClientTransactionEnd,
  CoreClientTransactionStart,
  CorePerfMetric,
} from '@/app/web-workers/quadraticCore/coreClientMessages';
import EventEmitter from 'eventemitter3';
import type { Point, Rectangle } from 'pixi.js';
import type { Content, Context, FileContent } from 'quadratic-shared/typesAndSchemasAI';

export interface DirtyObject {
  gridLines?: boolean;
  headings?: boolean;
  cursor?: boolean;
  cellHighlights?: boolean;
  multiplayerCursor?: boolean;
  boxCells?: boolean;
  cellMoving?: boolean;
  cellImages?: boolean;
  viewport?: boolean;
}

interface EventTypes {
  needRefresh: (state: RefreshType) => void;

  search: (found?: SheetPosTS[], current?: number) => void;
  hoverCell: (cell?: JsRenderCodeCell | EditingCell | ErrorValidation) => void;
  // the mouse entered (cell) or left (undefined) a cell with an open comment thread
  hoverComment: (cell?: { x: number; y: number }) => void;
  hoverTooltip: (rect?: Rectangle, text?: string, subtext?: string) => void;
  hoverLink: (link?: {
    x: number;
    y: number;
    url: string;
    rect: Rectangle;
    linkText?: string;
    isNakedUrl?: boolean;
    spanStart?: number;
    spanEnd?: number;
    // When set, anchor the popup to this rect (world coords) instead of the
    // cell's screen rect. Used to place the popup under the formula bar.
    anchorRect?: Rectangle;
    // When set, the popup is positioned in screen (client) coordinates via a
    // body-level portal instead of the grid's world overlay. Used by the formula
    // bar so the popup can extend over the AI analyst panel (outside the grid).
    screenRect?: Rectangle;
    // When set, the hover originated from an editor surface (formula bar or the
    // in-cell editor) rather than the grid. Editor hovers are allowed even while
    // the in-cell editor is open (grid hovers are suppressed during editing).
    fromEditor?: boolean;
  }) => void;
  insertLink: () => void;
  insertImageUrl: () => void;
  // Opens the Drive image picker. A target replaces the drive image
  // already at that position; omit it to insert at the cursor.
  insertImageFromDrive: (target?: { sheetId: string; x: number; y: number }) => void;
  // Opens the Drive file picker to import a data file at the cursor
  // (Insert > File). Handled by GridFileInput, which owns the import context.
  insertFileFromPicker: () => void;
  editImageUrl: (data: { sheetId: string; x: number; y: number; currentUrl: string }) => void;
  insertLinkInline: (data: { selectedText: string; selectionRange?: { start: number; end: number } }) => void;
  showInlineHyperlinkInput: (data: { selectedText: string; existingUrl?: string }) => void;
  inlineEditorCursorOnHyperlink: (data?: {
    url: string;
    rect: Rectangle;
    linkText: string;
    // When set, the popup is positioned in screen (client) coordinates via a
    // body-level portal instead of the grid's world overlay (formula bar).
    screenRect?: Rectangle;
  }) => void;
  hoverTable: (table?: JsRenderCodeCell) => void;

  zoom: (scale: number) => void;

  undoRedo: (undo: boolean, redo: boolean) => void;

  addSheet: (sheetInfo: SheetInfo, user: boolean) => void;
  deleteSheet: (sheetId: string, user: boolean) => void;
  sheetsInfo: (sheetInfo: SheetInfo[]) => void;
  sheetInfoUpdate: (sheetInfo: SheetInfo) => void;
  changeSheet: (sheetId: string) => void;
  sheetBounds: (sheetBounds: SheetBounds) => void;

  setCursor: (selection?: string) => void;
  cursorPosition: () => void;
  generateThumbnail: () => void;
  changeInput: (input: boolean, initialValue?: string, cursorMode?: CursorMode, options?: ChangeInputOptions) => void;
  headingSize: (width: number, height: number) => void;
  gridSettings: () => void;

  sheetOffsets: (sheetId: string, offsets: JsOffset[]) => void;
  sheetOffsetsUpdated: (sheetId: string) => void;
  sheetPrintSetup: (sheetId: string) => void;
  // the current sheet's page-break geometry changed
  pageBreaks: () => void;
  hashRenderFills: (hashRenderFills: Uint8Array) => void;
  hashesDirtyFills: (dirtyHashes: Uint8Array) => void;
  sheetMetaFills: (sheetId: string, fills: JsSheetFill[]) => void;
  htmlOutput: (html: JsHtmlOutput[]) => void;
  htmlUpdate: (html: JsHtmlOutput) => void;
  bordersSheet: (sheetId: string, borders: JsBordersSheet) => void;
  hashRenderCells: (sheetId: string, renderCells: JsRenderCell[]) => void;
  renderCodeCells: (sheetId: string, renderCodeCells: Uint8Array) => void;

  pythonInit: (version: string) => void;
  javascriptInit: (version: string) => void;
  pythonLoading: () => void;
  javascriptLoading: () => void;
  codeRunningState: (
    current?: CodeRun,
    awaitingExecution?: CodeRun[],
    updatingCharts?: { sheetId: string; chartId: string }[]
  ) => void;
  pendingCellsState: (cells: { sheetId: string; x: number; y: number; value?: string | null }[]) => void;

  updateCodeCells: (updateCodeCells: JsUpdateCodeCell[]) => void;
  requestCodeSummaries: (requests: JsCodeSummaryRequest[]) => void;
  updateImage: (message: CoreClientImage) => void;

  importProgress: (message: CoreClientImportProgress) => void;

  transactionStart: (message: CoreClientTransactionStart) => void;
  transactionEnd: (message: CoreClientTransactionEnd) => void;
  transactionEndUpdated: (transactionId: string) => void;
  cellEditHistory: (message: CoreClientCellEditHistory) => void;

  multiplayerUpdate: (users: MultiplayerUser[]) => void;
  multiplayerChangeSheet: () => void;
  multiplayerCursor: () => void;
  multiplayerState: (state: MultiplayerState) => void;
  multiplayerCellEdit: (cellEdit: CellEdit, player: MultiplayerUser) => void;
  multiplayerFollow: () => void;
  multiplayerAIChatPresence: () => void;
  multiplayerCodeRunning: (multiplayerUser: MultiplayerUser) => void;
  multiplayerCommentTyping: (multiplayerUser: MultiplayerUser) => void;
  multiplayerFileRename: (name: string) => void;
  multiplayerSynced: () => void;

  resizeHeadingColumn: (sheetId: string, column: number) => void;
  resizeHeadingRow: (sheetId: string, row: number) => void;

  offlineTransactions: (transactions: number, operations: number) => void;
  offlineTransactionsApplied: (timestamps: number[]) => void;

  connector: (query: string) => void;
  connectorResponse: (buffer: ArrayBuffer) => void;

  codeEditor: () => void;
  cellMoving: (move: boolean) => void;

  insertCodeEditorText: (text: string) => void;

  sheetValidations: (sheetId: string, validations: Validation[]) => void;
  sheetConditionalFormats: (sheetId: string, conditionalFormats: ConditionalFormatClient[]) => void;
  sheetComments: (sheetId: string, comments: CommentThread[]) => void;
  sheetCharts: (sheetId: string, charts: JsChart[]) => void;
  validationWarnings: (warnings: JsHashValidationWarnings[]) => void;

  // pointer down on the grid
  clickedToCell: (column: number, row: number, world: Point | true) => void;

  // dropdown button is pressed for dropdown Validation
  triggerCell: (column: number, row: number, forceOpen: boolean) => void;
  dropdownKeyboard: (key: 'ArrowDown' | 'ArrowUp' | 'Enter' | 'Escape') => void;

  // when validation changes state
  validation: (validation: string | boolean) => void;

  // trigger a context menu
  contextMenu: (options: ContextMenuState) => void;

  suggestionDropdownKeyboard: (key: 'ArrowDown' | 'ArrowUp' | 'Enter' | 'Escape' | 'Tab') => void;
  emojiDropdownKeyboard: (key: 'ArrowDown' | 'ArrowUp' | 'Enter' | 'Escape' | 'Tab') => void;

  // use this to set a drawing element to dirty
  viewportChanged: () => void;

  // use this only if you need to immediately get the viewport's value (ie, from React)
  viewportChangedReady: () => void;

  // use this to get the viewport's value after an update is complete
  viewportReadyAfterUpdate: () => void;

  hashContentChanged: (sheetId: string, hashX: number, hashY: number) => void;

  recentFiles: (url: string, name: string, loaded: boolean) => void;
  codeEditorCodeCell: (codeCell?: CodeCell) => void;

  a1Context: (context: Uint8Array) => void;
  a1ContextUpdated: () => void;

  aiAnalystInitialized: () => void;
  pixiAppSettingsInitialized: () => void;
  filesFromIframeInitialized: () => void;

  coreError: (from: string, error: Error | unknown) => void;

  bitmapFontsLoaded: () => void;

  dataTablesCache: (sheetId: string, dataTablesCache: SheetDataTablesCache) => void;
  dataTablesCacheRaw: (sheetId: string, dataTablesCache: Uint8Array) => void;
  contentCache: (sheetId: string, contentCache: SheetContentCache) => void;

  debugFlags: () => void;

  startupTimer: (name: TimerNames, data: { start?: number; end?: number }) => void;

  // Batches of quadratic-core wasm profiling samples relayed from the core worker.
  wasmPerfMetrics: (metrics: CorePerfMetric[]) => void;

  setDirty: (dirty: DirtyObject) => void;

  aiAnalystDroppedFiles: (files: FileList | File[]) => void;
  /**
   * Attach already-read files to the new-chat draft. Used for drive documents,
   * which are read from their download URL and carry a `documentUuid` (unlike
   * `aiAnalystDroppedFiles`, which reads local `File`s and uploads a drive copy).
   */
  aiAnalystAttachFiles: (files: FileContent[]) => void;
  /** Insert an inline mention pill (connection / table / code / sheet / cell) into the new-chat draft. */
  aiAnalystInsertMention: (mention: PromptEditorMention) => void;
  aiAnalystInsertText: (text: string) => void;
  aiAnalystOverwriteText: (text: string) => void;
  /** Briefly pulse the new-chat input to draw the user's attention. */
  aiAnalystFlashInput: () => void;
  aiAnalystReady: () => void;
  aiAnalystSubmitPrompt: (args: {
    content: Content;
    messageSource: string;
    context: Context;
    messageIndex: number;
    importFiles: ImportFile[];
  }) => void;
  mergeCells: (sheetId: string, mergeCells: JsMergeCells) => void;

  // Formatting button keyboard triggers (for visual feedback)
  formatButtonKeyboard: (action: string) => void;

  // Format painter events
  formatPainterStart: (sourceSelection: string, sourceSheetId: string) => void;
  formatPainterEnd: () => void;

  // Mobile events
  focusMobileEditBar: () => void;

  // File switching events
  fileCleanup: () => void;

  coreWasmReady: () => void;
}

export const events = new EventEmitter<EventTypes>();
